import React, { useState, useEffect } from 'react';
import {
  MdMenu, MdRssFeed, MdLock, MdBatteryFull, MdBatteryChargingFull,
  MdSignalCellular4Bar, MdSignalCellularConnectedNoInternet0Bar, MdWifiOff,
} from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { watchBattery, watchSignal } from '../lib/hardware';

const Header = ({ onToggleSidebar, isMobile, onChangePassword }) => {
  const { user } = useAuth();
  const [time, setTime] = useState(new Date());
  const [battery, setBattery] = useState({ supported: false, level: null, charging: false });
  const [signal, setSignal] = useState({ online: true, bars: 4, effectiveType: null });

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let unsubBatt, unsubSig;
    watchBattery(setBattery).then(u => { unsubBatt = u; });
    unsubSig = watchSignal(setSignal);
    return () => { unsubBatt?.(); unsubSig?.(); };
  }, []);

  const roleColors = {
    admin:    { bg: '#e8edf5', color: '#1a3c6e' },
    manager:  { bg: '#e3f2fd', color: '#1565c0' },
    operator: { bg: '#f0f4f8', color: '#546e7a' },
  };
  const roleStyle = roleColors[user?.role] || roleColors.operator;

  return (
    <header style={{
      height: 'var(--header-height)',
      background: 'white',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '0 12px' : '0 20px',
      gap: isMobile ? 10 : 16,
      flexShrink: 0,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      {/* Hamburger */}
      <button
        onClick={onToggleSidebar}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 22, display: 'flex',
          alignItems: 'center', padding: '4px', borderRadius: 6,
          transition: 'background 0.15s', flexShrink: 0,
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        <MdMenu />
      </button>

      {/* Title — uses the active tenant's display name from JWT, falls back
          to a generic label. Each tenant sees their own company name here. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <MdRssFeed style={{ color: 'var(--accent)', fontSize: 20, flexShrink: 0 }} />
        <span style={{
          fontWeight: 700, fontSize: isMobile ? 13 : 15,
          color: 'var(--primary)', letterSpacing: '0.2px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {isMobile
            ? (user?.tenant?.name || 'Assets')
            : (user?.tenant?.name ? `${user.tenant.name} — Asset Management` : 'Asset Management')}
        </span>
      </div>

      {/* Right side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
        {/* Connection signal */}
        <div title={`Network: ${signal.effectiveType || (signal.online ? 'online' : 'offline')}`}
          style={{ display: 'flex', alignItems: 'center', color: signal.online ? '#2e7d32' : '#c62828', fontSize: 18 }}>
          {!signal.online ? <MdWifiOff /> :
           signal.bars >= 3 ? <MdSignalCellular4Bar /> :
           <MdSignalCellularConnectedNoInternet0Bar />}
        </div>

        {/* Battery (only if supported) */}
        {battery.supported && (
          <div title={`Battery: ${battery.level}%${battery.charging ? ' (charging)' : ''}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
              color: battery.level < 20 ? '#c62828' : battery.level < 50 ? '#ef6c00' : '#2e7d32',
            }}>
            {battery.charging ? <MdBatteryChargingFull style={{ fontSize: 18 }} /> : <MdBatteryFull style={{ fontSize: 18 }} />}
            <span style={{ fontWeight: 600 }}>{battery.level}%</span>
          </div>
        )}

        {/* Clock — hide on small mobile */}
        {!isMobile && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {time.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        )}

        {!isMobile && <div style={{ width: 1, height: 28, background: 'var(--border)', flexShrink: 0 }} />}

        {/* User avatar + role */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'var(--primary)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
          </div>
          {!isMobile && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                {user?.full_name || user?.username}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                background: roleStyle.bg, color: roleStyle.color,
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                {user?.role}
              </span>
            </div>
          )}
          {onChangePassword && (
            <button
              onClick={onChangePassword}
              title="Change password"
              style={{
                background: 'none', border: '1px solid var(--border)', cursor: 'pointer',
                color: 'var(--text-secondary)', fontSize: 18, padding: '6px 8px',
                borderRadius: 6, display: 'flex', alignItems: 'center',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; e.currentTarget.style.color = 'var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <MdLock />
            </button>
          )}
          {isMobile && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
              background: roleStyle.bg, color: roleStyle.color,
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {user?.role}
            </span>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
